/**
 * Types d'erreurs métier pour la gestion unifiée des erreurs
 * Conformément à l'exigence 4.3 pour la cohérence de la gestion d'erreurs
 */

/**
 * Classe de base abstraite pour toutes les erreurs métier
 * Fournit une structure cohérente pour la gestion d'erreurs
 */
export abstract class BusinessError extends Error {
  abstract readonly code: string;
  abstract readonly userMessage: string;

  constructor(
    message: string,
    public readonly context?: Record<string, any>,
    public readonly field?: string
  ) {
    super(message);
    this.name = this.constructor.name;

    // Maintient la stack trace pour le débogage
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor);
    }
  }

  /**
   * Retourne une représentation JSON de l'erreur pour l'audit
   */
  toJSON(): Record<string, any> {
    return {
      name: this.name,
      code: this.code,
      message: this.message,
      userMessage: this.userMessage,
      field: this.field,
      context: this.context,
      timestamp: new Date().toISOString()
    };
  }
}

/**
 * Erreur de transition d'état invalide
 * Utilisée quand une transition d'état n'est pas autorisée
 */
export class StateTransitionError extends BusinessError {
  readonly code = 'STATE_TRANSITION_ERROR';
  readonly userMessage = 'Cette action n\'est pas autorisée dans l\'état actuel';

  constructor(
    fromState: string,
    toState: string,
    entityType: string,
    context?: Record<string, any>
  ) {
    super(
      `Transition invalide de ${fromState} vers ${toState} pour ${entityType}`,
      { fromState, toState, entityType, ...context }
    );
  }
}

/**
 * Erreur de validation des données
 * Utilisée quand les données ne respectent pas les règles de validation
 */
export class ValidationError extends BusinessError {
  readonly code = 'VALIDATION_ERROR';
  readonly userMessage = 'Les données fournies ne sont pas valides';

  constructor(
    message: string,
    field?: string,
    context?: Record<string, any>
  ) {
    super(message, context, field);
  }
}

/**
 * Erreur de violation de règle métier
 * Utilisée quand une action viole une règle métier définie
 */
export class BusinessRuleViolationError extends BusinessError {
  readonly code = 'BUSINESS_RULE_VIOLATION';
  readonly userMessage = 'Cette action viole une règle métier';

  constructor(
    ruleName: string,
    ruleDescription: string,
    context?: Record<string, any>
  ) {
    super(
      `Violation de la règle métier: ${ruleName} - ${ruleDescription}`,
      { ruleName, ruleDescription, ...context }
    );
  }
}

/**
 * Erreur de permissions insuffisantes
 * Utilisée quand l'utilisateur n'a pas les droits nécessaires
 */
export class PermissionDeniedError extends BusinessError {
  readonly code = 'PERMISSION_DENIED';
  readonly userMessage = 'Vous n\'avez pas les permissions nécessaires pour cette action';

  constructor(
    action: string,
    resource: string,
    userId?: string,
    context?: Record<string, any>
  ) {
    super(
      `Permission refusée pour l'action ${action} sur la ressource ${resource}`,
      { action, resource, userId, ...context }
    );
  }
}

/**
 * Erreur de ressource non trouvée
 * Utilisée quand une ressource demandée n'existe pas
 */
export class ResourceNotFoundError extends BusinessError {
  readonly code = 'RESOURCE_NOT_FOUND';
  readonly userMessage = 'La ressource demandée n\'a pas été trouvée';

  constructor(
    resourceType: string,
    resourceId: string,
    context?: Record<string, any>
  ) {
    super(
      `Ressource ${resourceType} avec l'ID ${resourceId} non trouvée`,
      { resourceType, resourceId, ...context }
    );
  }
}

/**
 * Erreur de conflit de données
 * Utilisée quand une opération entre en conflit avec l'état actuel des données
 */
export class DataConflictError extends BusinessError {
  readonly code = 'DATA_CONFLICT';
  readonly userMessage = 'Cette opération entre en conflit avec des données existantes';

  constructor(
    operation: string,
    conflictReason: string,
    context?: Record<string, any>
  ) {
    super(
      `Conflit de données lors de l'opération ${operation}: ${conflictReason}`,
      { operation, conflictReason, ...context }
    );
  }
}

/**
 * Erreur de limite dépassée
 * Utilisée quand une limite système est dépassée (ex: nombre de critères)
 */
export class LimitExceededError extends BusinessError {
  readonly code = 'LIMIT_EXCEEDED';
  readonly userMessage = 'Une limite système a été dépassée';

  constructor(
    limitType: string,
    currentValue: number,
    maxValue: number,
    context?: Record<string, any>
  ) {
    super(
      `Limite dépassée pour ${limitType}: ${currentValue} > ${maxValue}`,
      { limitType, currentValue, maxValue, ...context }
    );
  }
}

/**
 * Erreur de données requises manquantes
 * Utilisée quand des données obligatoires sont absentes
 */
export class RequiredDataMissingError extends BusinessError {
  readonly code = 'REQUIRED_DATA_MISSING';
  readonly userMessage = 'Des informations obligatoires sont manquantes';

  constructor(
    missingFields: string[],
    context?: Record<string, any>
  ) {
    super(
      `Champs obligatoires manquants: ${missingFields.join(', ')}`,
      { missingFields, ...context }
    );
  }
}

/**
 * Erreur de format de données invalide
 * Utilisée quand le format des données ne correspond pas aux attentes
 */
export class InvalidDataFormatError extends BusinessError {
  readonly code = 'INVALID_DATA_FORMAT';
  readonly userMessage = 'Le format des données n\'est pas valide';

  constructor(
    field: string,
    expectedFormat: string,
    actualValue: any,
    context?: Record<string, any>
  ) {
    super(
      `Format invalide pour le champ ${field}: attendu ${expectedFormat}, reçu ${typeof actualValue}`,
      { field, expectedFormat, actualValue, ...context },
      field
    );
  }
}

/**
 * Erreur de timeout d'opération
 * Utilisée quand une opération prend trop de temps
 */
export class OperationTimeoutError extends BusinessError {
  readonly code = 'OPERATION_TIMEOUT';
  readonly userMessage = 'L\'opération a pris trop de temps et a été interrompue';

  constructor(
    operation: string,
    timeoutMs: number,
    context?: Record<string, any>
  ) {
    super(
      `Timeout de l'opération ${operation} après ${timeoutMs}ms`,
      { operation, timeoutMs, ...context }
    );
  }
}

/**
 * Erreur de service externe
 * Utilisée quand un service externe retourne une erreur
 */
export class ExternalServiceError extends BusinessError {
  readonly code = 'EXTERNAL_SERVICE_ERROR';
  readonly userMessage = 'Un service externe a rencontré une erreur';

  constructor(
    serviceName: string,
    serviceError: string,
    context?: Record<string, any>
  ) {
    super(
      `Erreur du service externe ${serviceName}: ${serviceError}`,
      { serviceName, serviceError, ...context }
    );
  }
}

/**
 * Type union de toutes les erreurs métier
 * Utile pour le typage strict dans les gestionnaires d'erreurs
 */
export type AnyBusinessError =
  | StateTransitionError
  | ValidationError
  | BusinessRuleViolationError
  | PermissionDeniedError
  | ResourceNotFoundError
  | DataConflictError
  | LimitExceededError
  | RequiredDataMissingError
  | InvalidDataFormatError
  | OperationTimeoutError
  | ExternalServiceError;

/**
 * Garde de type pour vérifier si une erreur est une erreur métier
 */
export function isBusinessError(error: any): error is BusinessError {
  return error instanceof BusinessError;
}

/**
 * Utilitaire pour créer des erreurs de validation avec des messages traduits
 */
export class ValidationErrorFactory {
  static required(field: string): ValidationError {
    return new ValidationError(
      `Le champ ${field} est obligatoire`,
      field,
      { type: 'required' }
    );
  }

  static minLength(field: string, minLength: number, actualLength: number): ValidationError {
    return new ValidationError(
      `Le champ ${field} doit contenir au moins ${minLength} caractères (${actualLength} fournis)`,
      field,
      { type: 'minLength', minLength, actualLength }
    );
  }

  static maxLength(field: string, maxLength: number, actualLength: number): ValidationError {
    return new ValidationError(
      `Le champ ${field} ne peut pas dépasser ${maxLength} caractères (${actualLength} fournis)`,
      field,
      { type: 'maxLength', maxLength, actualLength }
    );
  }

  static invalidEmail(field: string, email: string): ValidationError {
    return new ValidationError(
      `L'adresse email ${email} n'est pas valide`,
      field,
      { type: 'invalidEmail', email }
    );
  }

  static arrayMinItems(field: string, minItems: number, actualItems: number): ValidationError {
    return new ValidationError(
      `Le champ ${field} doit contenir au moins ${minItems} éléments (${actualItems} fournis)`,
      field,
      { type: 'arrayMinItems', minItems, actualItems }
    );
  }

  static arrayMaxItems(field: string, maxItems: number, actualItems: number): ValidationError {
    return new ValidationError(
      `Le champ ${field} ne peut pas contenir plus de ${maxItems} éléments (${actualItems} fournis)`,
      field,
      { type: 'arrayMaxItems', maxItems, actualItems }
    );
  }
}